/**
 * Web update detection — the pure rules.
 *
 * The build stamps a version (git SHA + build time) into the bundle and into
 * a `version.json` deployed beside it. The running app periodically fetches
 * that file and compares. Two outcomes:
 *
 *  - a DIFFERENT build is available → surface it and apply at a quiet moment;
 *  - the running build is OLDER than `minSupportedBuildTime` → apply
 *    immediately (the ops lever for security/breaking fixes: edit the
 *    deployed version.json and every open tab converges within minutes).
 *
 * Versions are git SHAs and cannot be ordered, so ordering questions use the
 * build TIME. Kept platform-agnostic (no DOM) so the rules are unit-tested;
 * the polling, banner, and reload live in web-ui's AppUpdateService.
 */

export interface AppBuildStampModel {
	/** Git SHA (or other opaque id) of the build. */
	version: string;
	/** Epoch milliseconds the build was produced. */
	buildTime: number;
}

export interface DeployedVersionFileModel {
	version?: unknown;
	buildTime?: unknown;
	builtAt?: unknown;
	/**
	 * Builds produced BEFORE this epoch-ms must update immediately.
	 * Null/absent = no floor. Editable in place on the deployed file.
	 */
	minSupportedBuildTime?: unknown;
}

export interface AppUpdateVerdictModel {
	/** A build other than the running one is deployed. */
	updateAvailable: boolean;
	/** The running build is below the minimum-supported floor. */
	forceUpdate: boolean;
}

const toFiniteNumber = (value: unknown): number | null => {
	const parsed = typeof value === 'string' ? Number(value) : value;
	return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
};

/**
 * Compare the running build against the deployed version file.
 *
 * A malformed or partial file yields "no update": this runs on a timer in
 * every session, and a CDN hiccup or a half-written file must never be able
 * to reload the fleet.
 */
export function evaluateAppUpdate(
	running: AppBuildStampModel,
	deployed: DeployedVersionFileModel | null | undefined,
): AppUpdateVerdictModel {
	const none: AppUpdateVerdictModel = {
		updateAvailable: false,
		forceUpdate: false,
	};

	if (deployed == null || typeof deployed !== 'object') {
		return none;
	}

	const deployedVersion =
		typeof deployed.version === 'string' && deployed.version !== ''
			? deployed.version
			: null;

	if (deployedVersion == null) {
		return none;
	}

	const updateAvailable = deployedVersion !== running.version;

	const minSupported = toFiniteNumber(deployed.minSupportedBuildTime);
	const forceUpdate =
		updateAvailable &&
		minSupported != null &&
		Number.isFinite(running.buildTime) &&
		running.buildTime < minSupported;

	return { updateAvailable, forceUpdate };
}
